import React, { useMemo, useState } from 'react';

import { Alert, Box, CircularProgress, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import type { ChainWeightingMode, ParityChainTopProperty } from '~/types/services/priceParrot';

import { PARITY_SECTION_BLOCK_MB, SECTION_SX } from '../PriceParrotParityTool/constants';
import TogglePillGroup from '../shared/TogglePillGroup';

import { fmtAud, fmtInt, fmtPct } from './utils';

type TableView = 'internal' | 'external';

export interface PeriodPreset {
  key: string;
  label: string;
  days: number;
}

interface Props {
  rows: Array<ParityChainTopProperty>;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  /** Period pill row (mirrored from the rollup so the table card surfaces the same control). */
  periodPresets: ReadonlyArray<PeriodPreset>;
  activePresetKey: string | null;
  onPeriodPresetChange: (days: number) => void;
  /** Drives the ranking caption — worst-overpriced ranking semantics depend on weighting. */
  weighting: ChainWeightingMode;
  /** Total properties in the chain over the current range. */
  totalPropertyCount?: number;
}

const RANK_COL: GridColDef<ParityChainTopProperty> = {
  field: '__rank',
  headerName: '#',
  width: 60,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  renderCell: (params: GridRenderCellParams<ParityChainTopProperty>) => {
    const index = params.api.getRowIndexRelativeToVisibleRows(params.id);
    return index >= 0 ? String(index + 1) : '';
  },
};

const PROPERTY_COL: GridColDef<ParityChainTopProperty> = {
  field: 'property_name',
  headerName: 'PROPERTY',
  flex: 2,
  minWidth: 220,
  valueGetter: (_value, row) => row.property_name ?? row.offer_bk,
  renderCell: (params) => <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{params.value as string}</Typography>,
};

const REGION_COL: GridColDef<ParityChainTopProperty> = {
  field: 'continent',
  headerName: 'REGION',
  width: 120,
  valueGetter: (_value, row) => row.continent ?? '—',
};

const OVERPRICED_IMPRESSIONS_COL: GridColDef<ParityChainTopProperty> = {
  field: 'jobs_out_of_parity',
  headerName: 'OVERPRICED IMPRESSIONS',
  width: 180,
  type: 'number',
  valueFormatter: (value) => fmtInt(value as number),
};

const TOTAL_JOBS_COL: GridColDef<ParityChainTopProperty> = {
  field: 'total_jobs',
  headerName: 'SCRAPES',
  width: 120,
  type: 'number',
  valueFormatter: (value) => fmtInt(value as number),
};

const PCT_OOP_COL: GridColDef<ParityChainTopProperty> = {
  field: 'pct_out_of_parity',
  headerName: 'OUT OF PARITY %',
  width: 150,
  type: 'number',
  valueGetter: (_value, row) => row.pct_out_of_parity ?? null,
  valueFormatter: (value) => fmtPct(value as number | null),
};

const MARGIN_COL: GridColDef<ParityChainTopProperty> = {
  field: 'margin_aud',
  headerName: 'MARGIN',
  width: 130,
  type: 'number',
  valueFormatter: (value) => fmtAud(value as number),
};

const AVG_PREMIUM_COL: GridColDef<ParityChainTopProperty> = {
  field: 'avg_price_diff',
  headerName: 'AVG PREMIUM %',
  width: 140,
  type: 'number',
  // chain rollup table is overpriced-only, so the value is positive; render unsigned.
  valueGetter: (_value, row) => (row.avg_price_diff == null ? null : Math.abs(row.avg_price_diff)),
  valueFormatter: (value) => fmtPct(value as number | null),
};

const LPC_BEDBANK_COL: GridColDef<ParityChainTopProperty> = {
  field: 'bedbank_lpc_ratio',
  headerName: 'LPC : BEDBANK %',
  width: 150,
  type: 'number',
  valueGetter: (_value, row) => row.bedbank_lpc_ratio ?? null,
  valueFormatter: (value) => fmtPct(value as number | null),
};

const REV_AT_RISK_COL: GridColDef<ParityChainTopProperty> = {
  field: 'revenue_at_risk_aud',
  headerName: 'REVENUE AT RISK',
  width: 160,
  type: 'number',
  valueGetter: (_value, row) => row.revenue_at_risk_aud ?? null,
  valueFormatter: (value) => fmtAud(value as number | null),
};

/** Internal: surfaces LPC mix + margin the partner shouldn't see. */
const INTERNAL_COLUMNS: Array<GridColDef<ParityChainTopProperty>> = [
  RANK_COL,
  PROPERTY_COL,
  REGION_COL,
  OVERPRICED_IMPRESSIONS_COL,
  TOTAL_JOBS_COL,
  PCT_OOP_COL,
  AVG_PREMIUM_COL,
  LPC_BEDBANK_COL,
  MARGIN_COL,
  REV_AT_RISK_COL,
];

/** External: same parity signal but drops LPC mix, margin, and the raw scrape count
 * (partners shouldn't see how thin our sample is for a given property). */
const EXTERNAL_COLUMNS: Array<GridColDef<ParityChainTopProperty>> = [
  RANK_COL,
  PROPERTY_COL,
  REGION_COL,
  OVERPRICED_IMPRESSIONS_COL,
  PCT_OOP_COL,
  AVG_PREMIUM_COL,
  REV_AT_RISK_COL,
];

// Connected segmented control — black "Internal" selection, matches the rollup mode pill.
const modePillSx = {
  bgcolor: 'background.paper',
  borderRadius: 2,
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    px: 2,
    py: 0.75,
    fontWeight: 600,
    textTransform: 'none',
    color: 'text.primary',
    '&:hover': { bgcolor: 'background.paper' },
    '&.Mui-selected, &.Mui-selected:hover': {
      bgcolor: 'grey.900',
      color: 'common.white',
    },
  },
} as const;

export default function PropertiesTable({
  rows,
  isLoading,
  isError,
  errorMessage,
  periodPresets,
  activePresetKey,
  onPeriodPresetChange,
  weighting,
  totalPropertyCount,
}: Props) {
  const [view, setView] = useState<TableView>('internal');

  // svc-vendor only computes revenue_at_risk_aud + bedbank_lpc_ratio for the 30d window
  // (the Control Centre flag horizon). Outside 30d those fields are null, so hide the
  // columns rather than show a wall of '—'.
  const is30dPeriod = activePresetKey === '30d';

  const columns = useMemo(() => {
    const base = view === 'external' ? EXTERNAL_COLUMNS : INTERNAL_COLUMNS;
    if (is30dPeriod) {
      return base;
    }
    return base.filter((c) => c.field !== 'revenue_at_risk_aud' && c.field !== 'bedbank_lpc_ratio');
  }, [view, is30dPeriod]);

  const title = `Worst ${rows.length || 15} hotels (${view})`;

  const totalClause =
    totalPropertyCount != null ? `all ${totalPropertyCount} chain properties` : 'all chain properties';
  const viewClause =
    view === 'internal'
      ? 'Internal view includes margin, LPC : Bedbank ratio, and the scrape count.'
      : 'External view hides margin, LPC : Bedbank ratio, and the scrape count — safe to share with the chain.';

  return (
    <Paper elevation={0} sx={SECTION_SX}>
      <Box sx={{ mb: PARITY_SECTION_BLOCK_MB }}>
        <Typography sx={{ fontSize: 24, fontWeight: 600 }}>{title}</Typography>
        <Stack spacing={0.75} sx={{ mt: 0.5, maxWidth: 760 }}>
          {weighting === 'impressions' ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              <strong>Ranking by overpriced impressions</strong> — properties with the most overpriced scrapes first
              (ties broken by total scrapes). Switch the page weighting to <strong>margin</strong> to re-rank by
              margin-weighted overpriced volume: a small property losing 10 high-margin bookings can outrank one losing
              1,000 low-margin bookings.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              <strong>Ranking by margin-weighted overpriced impressions</strong> — properties leaving the most money on
              the table first (ties broken by overpriced impressions, then total scrapes). A small property losing 10
              high-margin bookings outranks one losing 1,000 low-margin bookings. Switch the page weighting to{' '}
              <strong>impressions</strong> to re-rank by raw overpriced volume instead.
            </Typography>
          )}
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Showing <strong>{rows.length}</strong> of {totalClause}. {viewClause} Revenue at Risk and LPC : Bedbank
            populate only when the 30d period is selected.
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, px: 2, py: 1.5, mb: PARITY_SECTION_BLOCK_MB }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 4 }}
          alignItems={{ md: 'center' }}
          useFlexGap
          flexWrap="wrap"
        >
          <TogglePillGroup
            label="Period"
            ariaLabel="properties period"
            options={periodPresets.map((preset) => ({ value: preset.key, label: preset.label }))}
            value={activePresetKey ?? ''}
            onChange={(key) => {
              const preset = periodPresets.find((p) => p.key === key);
              if (preset) {
                onPeriodPresetChange(preset.days);
              }
            }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mr: 1 }}>View</Typography>
            <ToggleButtonGroup
              value={view}
              exclusive
              size="small"
              onChange={(_, v) => v && setView(v as TableView)}
              aria-label="table view"
              sx={modePillSx}
            >
              <ToggleButton value="internal">Internal</ToggleButton>
              <ToggleButton value="external">External</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Box>

      {isError && errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ minHeight: 420 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.offer_bk}
            disableRowSelectionOnClick
            density="compact"
            // Rows arrive pre-sorted from svc-vendor under the active weighting; don't override
            // with a client-side default. Users can still click any column header to re-sort.
            pageSizeOptions={[15, 25, 50]}
            sx={{
              minHeight: 420,
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: 'text.secondary',
              },
            }}
          />
        )}
      </Box>
    </Paper>
  );
}
